import React, { forwardRef } from "react";
import styled from "styled-components";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

const MediaWrapper = styled.div`
  width: 100%;
  height: ${({ $autoHeight }) => ($autoHeight ? "auto" : "100%")};
  overflow: ${({ $autoHeight }) => ($autoHeight ? "visible" : "hidden")};
  position: relative;

  ${({ $showOverlay }) =>
    $showOverlay &&
    `
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
          180deg,
          rgba(0, 0, 0, 0) 41.37%,
          rgba(0, 0, 0, 0.5) 100%
        ),
        rgba(0, 0, 0, 0.5);
      pointer-events: none;
    }
  `}

  ${({ $autoHeight }) =>
    $autoHeight &&
    `
    .gatsby-image-wrapper {
      height: auto !important;
    }

    .gatsby-image-wrapper > div {
      display: none !important;
    }

    .gatsby-image-wrapper img {
      position: static !important;
      height: auto !important;
      width: 100% !important;
    }
  `}
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  width: 100%;
  height: ${({ $autoHeight }) => ($autoHeight ? "auto" : "100%")};
`;

const StyledVideo = styled.video`
  width: 100%;
  height: ${({ $autoHeight }) => ($autoHeight ? "auto" : "100%")};
  object-fit: ${({ $fit }) => $fit};
  display: block;
`;

const Media = forwardRef(
  (
    {
      media,
      alt = "Background media",
      className,
      showOverlay = false,
      fit = "cover",
      autoHeight = false,
    },
    ref,
  ) => {
    // Handle Strapi media structure
    let mediaSource = media;
    let image = null;

    // Check if media has Strapi structure (localFile)
    if (media?.localFile) {
      // Try to get Gatsby image data first (for images)
      if (media.localFile.childImageSharp?.gatsbyImageData) {
        image = getImage(media.localFile.childImageSharp.gatsbyImageData);
      } else {
        // Use publicURL for videos or non-optimized images
        mediaSource = media.localFile.url;
      }
    } else {
      // Direct media (could be string URL or Gatsby image object)
      image = getImage(media);
    }

    // Check if mediaSource is a video URL (case-insensitive and query/hash-safe)
    const isVideo =
      typeof mediaSource === "string" &&
      /\.(mp4|webm|ogg|mov)(\?.*)?(#.*)?$/i.test(mediaSource);

    return (
      <MediaWrapper
        ref={ref}
        className={className}
        $showOverlay={showOverlay}
        $autoHeight={autoHeight}
      >
        {isVideo ? (
          <StyledVideo
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            $fit={fit}
            $autoHeight={autoHeight}
          >
            <source src={mediaSource} type="video/mp4" />
            Your browser does not support the video tag.
          </StyledVideo>
        ) : image ? (
          <StyledGatsbyImage
            image={image}
            alt={alt}
            $autoHeight={autoHeight}
            objectFit={fit}
            objectPosition="center"
            imgStyle={{
              objectFit: fit,
              objectPosition: "center",
              width: "100%",
              height: autoHeight ? "auto" : "100%",
            }}
            style={{
              width: "100%",
              height: autoHeight ? "auto" : "100%",
            }}
          />
        ) : (
          // Fallback for static images
          <img
            src={typeof mediaSource === "string" ? mediaSource : media}
            alt={alt}
            style={{
              width: "100%",
              height: autoHeight ? "auto" : "100%",
              objectFit: fit,
              display: "block",
            }}
          />
        )}
      </MediaWrapper>
    );
  },
);

export default Media;
